import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { Link } from 'react-router-dom';

const { FiX, FiMinus, FiPlus, FiTrash2, FiShoppingBag } = FiIcons;

export default function CartDrawer({ isOpen, onClose, cart, updateQuantity, removeFromCart }) {
  const subtotal = cart.reduce((acc, item) => acc + (item.price * item.quantity), 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-smoke/80 backdrop-blur-sm z-[60]"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-smoke-light z-[70] shadow-2xl flex flex-col border-l border-white/5"
          >
            <div className="p-8 border-b border-white/5 flex items-center justify-between">
              <h2 className="font-serif text-2xl text-linen">Your Library</h2>
              <button onClick={onClose} className="p-2 text-linen/40 hover:text-ember transition-colors">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-grow overflow-y-auto p-8 custom-scrollbar">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-6 text-linen/20">
                    <SafeIcon icon={FiShoppingBag} className="w-8 h-8" />
                  </div>
                  <p className="text-linen/40 font-serif text-lg italic mb-8">The library is currently empty.</p>
                  <button 
                    onClick={onClose}
                    className="text-ember uppercase tracking-[0.2em] text-[10px] font-bold border-b border-ember pb-1"
                  >
                    Continue Browsing
                  </button>
                </div>
              ) : (
                <div className="space-y-8">
                  {cart.map((item) => (
                    <div key={item.id} className="flex gap-6 group">
                      <div className="w-24 h-32 bg-smoke-lighter flex-shrink-0 overflow-hidden">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                      </div>
                      <div className="flex-grow flex flex-col justify-between py-1">
                        <div>
                          <div className="flex justify-between items-start mb-1">
                            <h3 className="font-serif text-lg text-linen">{item.name}</h3>
                            <button 
                              onClick={() => removeFromCart(item.id)}
                              className="text-linen/20 hover:text-red-500 transition-colors"
                            >
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-[10px] text-ember uppercase tracking-widest mb-4">{item.family}</p>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center border border-white/10">
                            <button 
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="p-2 text-linen/40 hover:text-linen transition-colors"
                            >
                              <SafeIcon icon={FiMinus} className="w-3 h-3" />
                            </button>
                            <span className="w-8 text-center text-xs font-medium text-linen">{item.quantity}</span>
                            <button 
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="p-2 text-linen/40 hover:text-linen transition-colors"
                            >
                              <SafeIcon icon={FiPlus} className="w-3 h-3" />
                            </button>
                          </div>
                          <span className="text-sm font-medium text-linen/60">${(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-8 border-t border-white/5 bg-smoke-lighter/30">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-linen/40 uppercase tracking-widest text-[10px] font-bold">Subtotal</span>
                  <span className="text-xl font-serif text-linen">${subtotal.toFixed(2)}</span>
                </div>
                <Link 
                  to="/checkout"
                  onClick={onClose}
                  className="w-full block text-center bg-linen text-smoke py-5 uppercase tracking-[0.2em] text-[11px] font-bold hover:bg-ember hover:text-linen transition-all duration-500 shadow-xl"
                >
                  Proceed to Checkout
                </Link>
                <p className="text-center text-[9px] text-linen/30 mt-4 uppercase tracking-widest">
                  Shipping and taxes calculated at checkout
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}